import type { CredibilityTier } from '../types/contact'

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
}

export interface MeResponse {
  id: string
  username: string
  email: string
  role: string
  credibility_tier: CredibilityTier
  professional_request_status: 'none' | 'pending' | 'approved' | 'rejected'
  is_active: boolean
}

async function unwrap<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { detail?: string }).detail ?? fallback)
  }
  return res.json()
}

export async function register(
  username: string,
  email: string,
  password: string,
): Promise<MeResponse> {
  const res = await fetch(`${BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, email, password }),
  })
  return unwrap<MeResponse>(res, 'Registration failed')
}

export async function login(username: string, password: string): Promise<TokenResponse> {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  return unwrap<TokenResponse>(res, 'Login failed')
}

export async function getMe(token: string): Promise<MeResponse> {
  const res = await fetch(`${BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return unwrap<MeResponse>(res, 'Unauthorized')
}

export async function requestProfessionalTier(
  token: string,
  professionalDetails: string,
): Promise<MeResponse> {
  const res = await fetch(`${BASE}/auth/request-professional-tier`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ professional_details: professionalDetails }),
  })
  return unwrap<MeResponse>(res, 'Failed to submit professional-tier request')
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const res = await fetch(`${BASE}/auth/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  return unwrap<{ message: string }>(res, 'Failed to request password reset')
}

export async function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  const res = await fetch(`${BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  })
  return unwrap<{ message: string }>(res, 'Failed to reset password')
}
